import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../Components/Navbar";
import { Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import Logo from '../../assets/Images/Logo.png';
import api from "../../api/axiosConfig"; 

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const processedRef = useRef(false);
  const mountRef = useRef(false);
  const { t } = useTranslation('Login');

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "846376387992-4nh4a7cs4195n25ok2alcfk0293j19ue.apps.googleusercontent.com";

  const getRedirectUri = () => {
    return `${window.location.origin}/login`;
  };

  const handleOAuthCallback = useCallback(async (code, state) => {
    setGoogleLoading(true);
    setError(null);

    const savedState = localStorage.getItem('oauth_state');
    if (savedState && state !== savedState) {
      console.error("State mismatch:", { received: state, saved: savedState });
      setError("Security error: Invalid OAuth state.");
      setGoogleLoading(false);
      localStorage.removeItem('oauth_state');
      return;
    }
    localStorage.removeItem('oauth_state');

    try {
      // Use `api`, not `axios`
      await api.get('/get-csrf/');

      const redirectUri = getRedirectUri();

      const response = await api.post('/auth/google/', {
        code,
        redirect_uri: redirectUri,
        client_id: clientId,
        state,
      });

      if (response.status === 200 && response.data.success && response.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("Authenticated", "true");
        if (response.data.session_key) {
          localStorage.setItem('session_key', response.data.session_key);
        }
        if (response.data.google_access_token) {
          localStorage.setItem('google_access_token', response.data.google_access_token);
        }
        if (response.data.google_refresh_token) {
          localStorage.setItem('google_refresh_token', response.data.google_refresh_token);
        }

        setTimeout(() => {
          navigate("/homedash", { replace: true, state: { freshLogin: true } });
        }, 300);
      } else {
        throw new Error(response.data.error || "Auth failed");
      }
    } catch (error) {
      if (error.response?.status === 403 && error.response.data?.code === 'account_not_found') {
        navigate('/signup', { state: { googleEmail: error.response.data.email } });
        return;
      }
      setError(error.response?.data?.error || "Google login failed.");
    } finally {
      setGoogleLoading(false);
      setIsProcessing(false);
      processedRef.current = false;
    }
  }, [clientId, navigate]);

  useEffect(() => {
    if (mountRef.current) return;
    mountRef.current = true;

    if (processedRef.current || isProcessing) return;

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      setError(`Google login failed: ${error}`);
      window.history.replaceState({}, document.title, '/login');
    } else if (code && !processedRef.current) {
      processedRef.current = true;
      setIsProcessing(true);
      setGoogleLoading(true);
      handleOAuthCallback(code, state);
    }
  }, [location, handleOAuthCallback, isProcessing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Use `api` — CSRF cookie is fetched automatically
      await api.get('/get-csrf/');

      const response = await api.post('/login/', {
        email: email.trim(),
        password,
      });

      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("Authenticated", "true");
      if (response.data.session_key) {
        localStorage.setItem('session_key', response.data.session_key);
      }

      setTimeout(() => {
        navigate("/homedash", { replace: true, state: { freshLogin: true } });
      }, 200);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  function getErrorMessage(error) {
    if (error.code === 'ECONNABORTED') return t('login.errors.timeout');
    if (error.response?.status === 400) return t('login.errors.invalidCredentials');
    if (error.response?.status === 403) return t('login.errors.securityToken');
    if (error.response?.status === 429) return t('login.errors.rateLimit');
    if (!error.response) return t('login.errors.network');
    return t('login.errors.generic');
  }

  const handleGoogleLogin = () => {
    if (!clientId) {
      setError(t('login.google.errors.clientId'));
      return;
    }

    setGoogleLoading(true);
    setError(null);

    try {
      const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('oauth_state', state);

      const redirectUri = getRedirectUri();

      // Remove trailing space!
      const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

      const params = {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        state: state,
        prompt: 'select_account'
      };

      Object.keys(params).forEach(key =>
        googleAuthUrl.searchParams.append(key, params[key])
      );

      window.location.href = googleAuthUrl.toString();
    } catch (error) {
      console.error("Google OAuth error:", error);
      setError(t('login.google.errors.initFailed'));
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        const user = localStorage.getItem('user');
        const isAuthenticated = localStorage.getItem('Authenticated') === 'true';

        if (user && isAuthenticated) {
          const response = await api.get('/auth/status/');
          if (response.data.authenticated) {
            navigate("/homedash", { replace: true });
            return;
          }
        }
        localStorage.clear();
      } catch (error) {
        console.error("Auth check failed:", error);
        localStorage.clear();
      }
    };

    if (!isProcessing && !processedRef.current) {
      checkExistingAuth();
    }
  }, [navigate, isProcessing]);

  return (
    <div className="flex justify-center items-center h-screen bg-[var(--bg-black)] from-purple-900 via-black to-purple-900">
      <div className="absolute top-0 left-0 w-full">
        <Navbar minimal />
      </div>
      <div className="bg-[var(--bg-black)] border border-[var(--border)] border-solid p-8 rounded-2xl shadow-xl w-96 text-[var(--text-black)] text-center mt-20">
        <div className="flex justify-center bg-[var(--bg-black)]">
          <img src={Logo} alt="Logo" className="h-12 w-12 bg-transparent" />
        </div>

        <h2 className="text-2xl font-bold mt-4 bg-[var(--bg-black)] text-[var(--text-black)]">{t('login.title')}</h2>
        <p className="text-gray-400 mt-2 text-5 bg-[var(--bg-black)]">
          {t('login.signupPrompt')}
          <Link to="/signup" className="text-pink-500 font-bold cursor-pointer ml-1 no-underline hover:underline bg-transparent">
            {t('login.signupLink')}
          </Link>
        </p>

        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[var(--text-black)] mt-2 bg-pink-500 p-2 rounded-lg"
          >
            {error}
          </motion.p>
        )}

        <form onSubmit={handleSubmit} className="mt-6">
          <input
            type="email"
            placeholder={t('login.form.email')}
            className="w-full px-4 py-2 mb-4 bg-[var(--bg-black)] border-[var(--border)] text-[var(--text-black)] rounded-lg border border-solid focus:outline-none"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || googleLoading}
            required
          />

          <div className="relative bg-transparent">
            <input
              type={passwordVisible ? "text" : "password"}
              placeholder={t('login.form.password')}
              className="w-full px-4 py-2 mb-4 bg-[var(--bg-black)] text-[var(--text-black)] rounded-lg border-[var(--border)] border border-solid focus:outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading || googleLoading}
              required
            />
            <span
              onClick={() => setPasswordVisible(!passwordVisible)}
              className="absolute right-2 -mt-4 top-1/2 -translate-y-1/2 text-[var(--text-black)] cursor-pointer"
            >
              {passwordVisible ? <FaEyeSlash color="white text-[var(--text-black)]" /> : <FaEye color="white text-[var(--text-black)]" />}
            </span>
          </div>

          <div className="flex justify-end bg-transparent">
            <a href="/forgot-password" className="text-pink-500 text-mm font-bold cursor-pointer bg-transparent no-underline mb-2">
              {t('login.forgotPassword')}
            </a>
          </div>

          <motion.button
            animate={{
              background: [
                "linear-gradient(90deg, #FF1493, #FF69B4)",
                "linear-gradient(90deg, #FF6347, #FF1493)",
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="w-full bg-pink-500 hover:bg-pink-600 text-white py-1 rounded-lg text-lg font-semibold border border-none"
            type="submit"
            disabled={loading || googleLoading}
          >
            {loading ? t('login.form.submitting') : t('login.form.submit')}
          </motion.button>
        </form>

        <div className="flex items-center mt-4 bg-transparent">
          <hr className="flex-grow border-gray-600" />
          <span className="mx-2 text-[var(--text-black)] bg-[var(--bg-black)]">{t('login.divider')}</span>
          <hr className="flex-grow border-[var(--border)]" />
        </div>

        <div className="flex justify-center bg-transparent">
          {googleLoading ? (
            <div className="flex justify-center items-center py-4 text-[var(--text-black)] bg-[var(--bg-black)] border border-[var(--border)] rounded-lg w-full">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00FF1E]"></div>
              <span className="ml-3 text-sm">
                {window.location.search.includes('code') ? t('login.google.loading.processing') : t('login.google.loading.redirecting')}
              </span>
            </div>
          ) : (
            <button
              onClick={handleGoogleLogin}
              disabled={loading || googleLoading}
              className="w-full bg-[var(--bg-black)] text-[var(--text-black)] py-3 px-4 cursor-pointer rounded-lg font-semibold border border-[var(--border)] flex items-center justify-center space-x-2 transition-colors duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{t('login.google.button')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;